import { useEffect } from 'react';
import { BProgress } from '@bprogress/core';
import '@bprogress/core/css';
import { useRouter } from 'next/router';

const IS_OFF = true;

/**
 * A React component that provides navigation progress feedback using `BProgress`.
 * It listens to route change events from the Next.js router and optionally the
 * `window.navigation` API to display a progress bar during navigation transitions.
 *
 * The progress bar starts/stops based on navigation events. It will not be displayed on hash-only changes in the URL.
 *
 * @returns `null` - This component does not render any UI elements directly. The progress bar is rendered by the BProgress library.
 *
 * @example
 * ```tsx
 * import { NavigationProgressProvider } from './NavigationProgressProvider';
 *
 * const App = () => (
 *     <>
 *         <NavigationProgressProvider />
 *         <MainContent />
 *     </>
 * );
 * ```
 */
export const NavigationProgressProvider = () => {
    const { events } = useRouter();
    useEffect(() => {
        if (IS_OFF) {
            return () => {
                // No need to set up event listeners if the progress bar is off
            };
        }

        BProgress.done();
        BProgress.configure({
            showSpinner: false
        });

        const loadingStartedHandler = (event: NavigateEvent) => {
            if (!event.destination || !event.destination.url) {
                // Exit early if the event does not have a destination property
                return;
            }
            const current = new URL(window.location.href);
            const destination = new URL(event.destination.url);

            const isHashOnlyChange =
                current.pathname === destination.pathname &&
                current.search === destination.search &&
                current.hash !== destination.hash;

            if (isHashOnlyChange) {
                return;
            }
            BProgress.start();
        };

        const loadingFinishedHandler = () => {
            BProgress.done();
        };

        events.on('routeChangeStart', loadingStartedHandler);
        events.on('routeChangeComplete', loadingFinishedHandler);
        events.on('routeChangeError', loadingFinishedHandler);

        window.navigation?.addEventListener('navigate', loadingStartedHandler);

        return () => {
            if (IS_OFF) {
                return;
            }
            events.off('routeChangeStart', loadingStartedHandler);
            events.off('routeChangeComplete', loadingFinishedHandler);
            events.off('routeChangeError', loadingFinishedHandler);

            window.navigation?.removeEventListener('navigate', loadingStartedHandler);
        };
    }, []);

    return null;
};
